import React, { useState, useEffect } from 'react';
import { authFetch, fetchCsrfToken } from './authFetch';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function DocumentList({ customerId }) {
  const [documents, setDocuments] = useState([]);
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const timeZone = 'America/New_York';

  useEffect(() => {
    fetchDocuments();
  }, [customerId]);

const fetchDocuments = async () => {
  try {
    const response = await authFetch(`${BASE_URL}/api/documents/?customer_id=${customerId}`);
    if (response.ok) {
      const data = await response.json();
      console.log("Documents fetched:", data);
      
      // FIXED: Handle paginated response format
      const processedDocuments = Array.isArray(data) ? data : (data?.results || []);
      console.log("Processed documents:", processedDocuments);
      setDocuments(processedDocuments);
    } else {
      console.error("Error fetching documents:", response.status);
    }
  } catch (err) {
    console.error("Error fetching documents:", err);
  }
};

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    console.log("File selected:", selectedFile ? selectedFile.name : "none");
    setFile(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    if (!description) {
      setError('Please provide a description for the file.');
      return;
    }
    
    setError('');
    setIsLoading(true);
    setUploadStatus('Preparing upload...');

    try {
      // Ensure we have a CSRF token first
      await fetchCsrfToken();
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);
      formData.append('customer', customerId);
      
      console.log("Uploading file:", file.name);
      console.log("Form data keys:", [...formData.keys()]);
      setUploadStatus('Uploading to server...');

      const response = await authFetch(`${BASE_URL}/api/documents/`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header, the browser will set it with the boundary
      });

      console.log("Upload response status:", response.status);
      
      if (!response.ok) {
        // Try to get more detailed error information
        try {
          const errorData = await response.json();
          console.error("Upload error details:", errorData);
          throw new Error(errorData.error || "Failed to upload file.");
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
          throw new Error(`Server error: ${response.status}`);
        }
      }
      
      // Success!
      const responseData = await response.json();
      console.log("Upload successful:", responseData);
      setUploadStatus('Upload complete!');
      
      // Reset form
      setFile(null);
      setDescription('');
      document.getElementById('file-input').value = '';
      
      // Refresh the documents list
      fetchDocuments();

      // Clear status after a delay
      setTimeout(() => {
        setUploadStatus('');
      }, 3000);

    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "An unexpected error occurred during upload.");
      setUploadStatus('Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6 bg-white p-4 border border-gray-200 rounded shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Upload New Document</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="e.g., Insurance Card (Front)"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
            <input
              type="file"
              id="file-input"
              onChange={handleFileChange}
              className="w-full"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          {uploadStatus && <p className="text-blue-500 text-sm mb-4">{uploadStatus}</p>}
          <button
            type="submit"
            disabled={isLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:bg-gray-400"
          >
            {isLoading ? 'Uploading...' : 'Upload Document'}
          </button>
        </form>
      </div>

      <h3 className="text-lg font-semibold mb-4">Uploaded Documents</h3>
      <ul className="space-y-3">
        {documents.length > 0 ? documents.map(doc => {
            const localTime = toZonedTime(new Date(doc.created_at), timeZone);
            const formattedDate = format(localTime, 'MM-dd-yyyy hh:mm a');
            return (
              <li key={doc.id} className="border border-gray-200 p-4 rounded flex justify-between items-center">
                <div>
                  <strong>{doc.description}</strong>
                  <p className="text-sm text-gray-600">Uploaded by {doc.created_by_username} on {formattedDate}</p>
                </div>
                <a 
                  href={doc.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition"
                >
                  View
                </a>
              </li>
            )
        }) : (
          <p>No documents have been uploaded for this customer.</p>
        )}
      </ul>
    </div>
  );
}

export default DocumentList;
